var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from "react";
import { MultiColumnFormFields } from "@components/common/form/FormFields";
import { PeekDialogActions, PeekDialogContent, PeekDivider, PeekPaper, PeekStack, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import { Form, Formik } from "formik";
import EditRequestActions from "./EditRequestActions";
import { EditRequestFormFields, EditRequestFormValidationSchema } from "./EditRequestFormConfig";
import { ScanRequestStatus, useError, useLoader, useNotification, useUpdateScanRequest, useRefetch, } from "@piiqtechnologies/ui-lib";
import { AccessDetailOption } from "../access-details";
import formatUTCDate from "../FormatUTCDate";
import { useIntl } from "react-intl";
import messages from "./messages.i18n";
const EditRequestInfo = ({ name, unit, address }) => {
    const theme = useTheme();
    return (React.createElement(PeekPaper, { variant: "outlined", sx: {
            alignItems: "center",
            p: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
            backgroundColor: "grey.50",
            borderRadius: theme.spacing(0.5),
        } },
        React.createElement(PeekStack, { direction: "column", spacing: 0.5 },
            React.createElement(PeekTypography, { variant: "body2", color: "grey.400", textTransform: "uppercase" }, name),
            React.createElement(PeekStack, { direction: "row" },
                React.createElement(PeekTypography, { variant: "h4", fontWeight: theme.typography.fontWeightMedium }, unit),
                React.createElement(PeekTypography, { variant: "h4" },
                    "\u00A0- ",
                    address)))));
};
export const EditRequestForm = ({ scanRequest, handleScanUpdate }) => {
    const theme = useTheme();
    const intl = useIntl();
    const { setLoader } = useLoader();
    const { setError } = useError();
    const { setNotification } = useNotification();
    const { setRefetch } = useRefetch();
    const [isCancelScanRequestForm, setIsCancelScanRequestForm] = useState(false);
    const [updateScanRequest, scanRequestUpdated, updateScanRequestLoading, updateScanRequestError] = useUpdateScanRequest();
    const toggleCancelRequestForm = () => {
        setIsCancelScanRequestForm(!isCancelScanRequestForm);
    };
    const onEditRequestForm = (values) => {
        if (isCancelScanRequestForm) {
            updateScanRequest({
                _id: scanRequest._id,
                reason: values.reason,
                status: ScanRequestStatus.CANCELED,
                cancellationReason: values.cancellationReason,
            });
            return;
        }
        const { _cancellationReason, _reason, accessDetails, accessDetailsSpecifications } = values, editProperties = __rest(values, ["_cancellationReason", "_reason", "accessDetails", "accessDetailsSpecifications"]);
        updateScanRequest(Object.assign({ _id: scanRequest._id, accessDetails: accessDetails === AccessDetailOption.OTHER ? accessDetailsSpecifications : AccessDetailOption.LEASING }, editProperties));
    };
    const EditRequestFormInitialValues = {
        availableFrom: formatUTCDate(scanRequest.availableFrom),
        accessDetails: scanRequest.accessDetails && scanRequest.accessDetails !== AccessDetailOption.LEASING
            ? AccessDetailOption.OTHER
            : AccessDetailOption.LEASING,
        accessDetailsSpecifications: scanRequest.accessDetails !== AccessDetailOption.LEASING ? scanRequest.accessDetails : "",
        specialInstructions: scanRequest.specialInstructions || "",
    };
    useEffect(() => {
        if (updateScanRequestError) {
            setError(updateScanRequestError.message);
        }
    }, [updateScanRequestError]);
    useEffect(() => {
        if (scanRequestUpdated) {
            handleScanUpdate();
            setNotification(intl.formatMessage(messages.updateRequestSuccess));
            setRefetch();
        }
    }, [scanRequestUpdated]);
    useEffect(() => {
        setLoader(updateScanRequestLoading, "UpdateScanInProgress");
        return () => setLoader(false, "UpdateScanInProgress");
    }, [updateScanRequestLoading]);
    return (React.createElement(Formik, { onSubmit: onEditRequestForm, initialValues: EditRequestFormInitialValues, validationSchema: !isCancelScanRequestForm && EditRequestFormValidationSchema() }, ({ values }) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        const { cancelRequest, editRequest } = EditRequestFormFields({
            showAccessDetailsSpecifications: values.accessDetails === AccessDetailOption.OTHER,
        });
        return (React.createElement(Form, null,
            React.createElement(PeekDialogContent, null,
                React.createElement(PeekStack, { direction: "column", spacing: 1.5, textAlign: "left" },
                    React.createElement(EditRequestInfo, { name: (_a = scanRequest.community) === null || _a === void 0 ? void 0 : _a.name, unit: (_b = scanRequest.space) === null || _b === void 0 ? void 0 : _b.unit, address: [
                            (_e = (_d = (_c = scanRequest.space) === null || _c === void 0 ? void 0 : _c.building) === null || _d === void 0 ? void 0 : _d.address) === null || _e === void 0 ? void 0 : _e.street,
                            (_h = (_g = (_f = scanRequest.space) === null || _f === void 0 ? void 0 : _f.building) === null || _g === void 0 ? void 0 : _g.address) === null || _h === void 0 ? void 0 : _h.city,
                            (_l = (_k = (_j = scanRequest.space) === null || _j === void 0 ? void 0 : _j.building) === null || _k === void 0 ? void 0 : _k.address) === null || _l === void 0 ? void 0 : _l.state,
                            (_p = (_o = (_m = scanRequest.space) === null || _m === void 0 ? void 0 : _m.building) === null || _o === void 0 ? void 0 : _o.address) === null || _p === void 0 ? void 0 : _p.postalCode,
                        ]
                            .filter(Boolean)
                            .join(", ") }),
                    React.createElement(PeekDivider, { orientation: "horizontal", light: true, sx: { pt: theme.spacing(1) } }),
                    React.createElement(MultiColumnFormFields, { fullWidth: true, fields: isCancelScanRequestForm ? cancelRequest : editRequest }))),
            React.createElement(PeekDialogActions, null,
                React.createElement(PeekStack, { direction: "column", gap: 1, width: "100%" },
                    React.createElement(EditRequestActions, { isCancelRequestForm: isCancelScanRequestForm, toggleCancelRequestForm: toggleCancelRequestForm })))));
    }));
};
export default EditRequestForm;
